import React, { useEffect, useMemo } from "react";
import { change as changeField, Field, formValueSelector } from "redux-form";
import { useDispatch, useSelector } from "react-redux";
import { components } from "react-select";

import { CustomCheckbox } from "../../../../../../../common/customCheckbox"
import { CustomSingleSelect } from "../../../../../../../common/new-core-ui/CustomSingleSelect";
import colorStyles from "../../../../FormSelectStyle";
import styles from "../../../../../../SwmsMasters/SwmsMastersStyle.module.css";
import layoutStyles from "../../../../../../SwmsMasters/EmergencyPlans/EmergencyPlansLayout.module.css";

const FORM_NAME = "JobSwmsV3EmergencyPlansContainerForm";
const SECTION_KEY = "rescue_equipment_staging";
const formSelector = formValueSelector(FORM_NAME);

const PARTS = [
  { key: "rescue_equipment", heading: "Rescue Equipment" },
  { key: "rescue_ppe", heading: "Rescue PPE" },
  { key: "licences_competencies", heading: "Licences & competencies" }
];

const normalizeTypeId = (value) => {
  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? id : null;
};

const isActive = (value) => Number(value) === 1;

const normalizeSelectedItems = (selectedItems) => {
  if (Array.isArray(selectedItems)) {
    return selectedItems.reduce((map, itemId) => {
      const normalizedId = normalizeTypeId(itemId);
      if (normalizedId) {
        map[normalizedId] = true;
      }
      return map;
    }, {});
  }

  if (selectedItems && typeof selectedItems === "object") {
    return Object.keys(selectedItems).reduce((map, itemId) => {
      if (selectedItems[itemId]) {
        const normalizedId = normalizeTypeId(itemId);
        if (normalizedId) {
          map[normalizedId] = true;
        }
      }
      return map;
    }, {});
  }

  return {};
};

const countSelectedItems = (selectedItems) => Object.keys(normalizeSelectedItems(selectedItems)).length;

const getPartTypeId = (partData) => {
  if (!partData || typeof partData !== "object") {
    return null;
  }

  const objectValue = partData.method_item_type && partData.method_item_type.value
    ? normalizeTypeId(partData.method_item_type.value)
    : null;
  if (objectValue) {
    return objectValue;
  }

  return normalizeTypeId(partData.method_item_type_id);
};

export default function JobSwmsV3RescueEquipmentStagingSection({
  loading,
  sectionData,
  planIndex,
  methodItemTypeGroups = [],
  methodItemTypesLoading = false
}) {
  const dispatch = useDispatch();

  const safeSectionData = sectionData && sectionData.data ? sectionData.data : (sectionData || {});
  const formData = useSelector((state) => formSelector(
    state,
    `plans[${planIndex}].sections.${SECTION_KEY}.data`
  ));
  const liveSectionData = formData || safeSectionData;

  useEffect(() => {
    if (!formData) {
      return;
    }

    PARTS.forEach((part) => {
      const selectedItems = formData[part.key] && formData[part.key].selected_items;
      const normalizedItems = normalizeSelectedItems(selectedItems);
      if (JSON.stringify(selectedItems) !== JSON.stringify(normalizedItems)) {
        dispatch(changeField(
          FORM_NAME,
          `plans[${planIndex}].sections.${SECTION_KEY}.data.${part.key}.selected_items`,
          normalizedItems
        ));
      }
    });
  }, [dispatch, formData, planIndex]);

  const groupedById = useMemo(() => {
    const map = {};
    (Array.isArray(methodItemTypeGroups) ? methodItemTypeGroups : []).forEach((group) => {
      const id = normalizeTypeId(group && group.method_item_type_id);
      if (!id) {
        return;
      }

      map[id] = {
        id,
        name: group && group.method_item_type ? String(group.method_item_type) : `Type ${id}`,
        active: isActive(group && group.active),
        items: (Array.isArray(group && group.method_items) ? group.method_items : []).map((item) => ({
          id: normalizeTypeId(item && item.id),
          name: item && item.method_item ? String(item.method_item) : "",
          active: isActive(item && item.active)
        })).filter((item) => item.id)
      };
    });
    return map;
  }, [methodItemTypeGroups]);

  const selectedTypeIdsByPart = useMemo(() => {
    const map = {};
    PARTS.forEach((part) => {
      map[part.key] = getPartTypeId(liveSectionData[part.key]);
    });
    return map;
  }, [liveSectionData]);

  const selectedItemIdsByPart = useMemo(() => {
    const map = {};
    PARTS.forEach((part) => {
      const partData = liveSectionData[part.key] && typeof liveSectionData[part.key] === "object"
        ? liveSectionData[part.key]
        : {};
      map[part.key] = new Set(Object.keys(normalizeSelectedItems(partData.selected_items)).map(Number));
    });
    return map;
  }, [liveSectionData]);

  const selectedTypeIds = useMemo(() => {
    return new Set(Object.values(selectedTypeIdsByPart).filter(Boolean));
  }, [selectedTypeIdsByPart]);

  const typeOptions = useMemo(() => {
    return Object.values(groupedById)
      .filter((group) => group.active || selectedTypeIds.has(group.id))
      .map((group) => ({
        value: group.id,
        label: group.name
      }));
  }, [groupedById, selectedTypeIds]);

  return (
    <div className="container-fluid pb-2">
      <div className="row pt-2">
        <div className="col-12">
          {PARTS.map((part) => {
            const partData = liveSectionData[part.key] && typeof liveSectionData[part.key] === "object"
              ? liveSectionData[part.key]
              : {};
            const selectedTypeId = selectedTypeIdsByPart[part.key];
            const selectedGroup = selectedTypeId ? groupedById[selectedTypeId] : null;
            const selectedItemIds = selectedItemIdsByPart[part.key] || new Set();
            const items = selectedGroup && Array.isArray(selectedGroup.items)
              ? selectedGroup.items.filter((item) => item.active || selectedItemIds.has(item.id))
              : [];
            const selectedItemCount = countSelectedItems(partData.selected_items);
            const otherSelectedTypeIds = PARTS
              .filter((candidate) => candidate.key !== part.key)
              .map((candidate) => selectedTypeIdsByPart[candidate.key])
              .filter(Boolean);
            const otherSelectedTypeSet = new Set(otherSelectedTypeIds);

            return (
              <div
                key={part.key}
                style={{
                  border: "1px solid #e4e0f0",
                  borderRadius: "8px",
                  background: "#fff",
                  marginBottom: "10px"
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", background: "#F0EEF7", padding: "7px 11px" }}>
                  <div className={styles.Label} style={{ marginBottom: 0, fontSize: "11px", fontWeight: 800, letterSpacing: "0.03em", textTransform: "uppercase" }}>{part.heading}</div>
                  <span
                    style={{
                      minWidth: "68px",
                      height: "18px",
                      borderRadius: "4px",
                      fontSize: "10px",
                      fontWeight: 700,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "0",
                      lineHeight: 1,
                      flexShrink: 0,
                      background: "transparent",
                      color: "#6B5B95"
                    }}
                    title={`${selectedItemCount} selected`}
                    aria-label={`${selectedItemCount} selected`}
                  >
                    {selectedItemCount} selected
                  </span>
                </div>

                <div style={{ padding: "9px 16px 0" }}>
                  <div style={{ maxWidth: "420px" }}>
                    <Field
                      name={`${part.key}.method_item_type`}
                      component={({ input, meta }) => (
                        <CustomSingleSelect
                          input={input}
                          meta={meta}
                          placeholder="Select method item type"
                          options={typeOptions.filter((option) => {
                            const optionTypeId = normalizeTypeId(option && option.value);
                            return optionTypeId === selectedTypeId || !otherSelectedTypeSet.has(optionTypeId);
                          })}
                          styles={colorStyles}
                          otherProps={{
                            hideSelectedOptions: false,
                            components: { dropdownIndicator: <components.DownChevron /> },
                            isLoading: methodItemTypesLoading,
                            isDisabled: false
                          }}
                          onChange={(nextOption) => {
                            input.onChange(nextOption || null);
                            dispatch(changeField(
                              FORM_NAME,
                              `plans[${planIndex}].sections.${SECTION_KEY}.data.${part.key}.method_item_type_id`,
                              nextOption && nextOption.value ? Number(nextOption.value) : null
                            ));
                            dispatch(changeField(
                              FORM_NAME,
                              `plans[${planIndex}].sections.${SECTION_KEY}.data.${part.key}.selected_items`,
                              {}
                            ));
                          }}
                        />
                      )}
                    />
                  </div>
                </div>

                {!selectedTypeId ? (
                  <div style={{ color: "#6b7280", fontSize: "12px", marginTop: "8px", marginBottom: "8px", paddingLeft: "16px", paddingRight: "16px" }}>
                    Select a method item type to load items.
                  </div>
                ) : null}

                {selectedTypeId && items.length === 0 ? (
                  <div style={{ color: "#6b7280", fontSize: "12px",  marginTop: "8px", marginBottom: "8px", paddingLeft: "16px", paddingRight: "16px"  }}>
                    No method items found for this type.
                  </div>
                ) : null}

                {selectedTypeId && items.length > 0 ? (
                  <div className="container-fluid" style={{ marginTop: "8px", padding: "0 16px 9px", maxHeight: "300px", overflow: "hidden auto" }}>
                    <div className="row" style={{ rowGap: "2px", columnGap: 0 }}>
                      {items.map((item) => {
                        const itemId = normalizeTypeId(item && item.id);
                        const itemName = item && item.name ? item.name : `Item ${itemId}`;

                        return (
                          <div key={itemId} className="col-12 col-md-6" style={{ marginTop: "2px", marginBottom: "2px", paddingLeft: "10px", paddingRight: "10px" }}>
                            <div
                              role="button"
                              className={layoutStyles.MethodItemOption}
                              tabIndex={loading || methodItemTypesLoading ? -1 : 0}
                              onClick={(event) => {
                                if (event.target && typeof event.target.closest === "function" && event.target.closest(".ant-checkbox-wrapper")) {
                                  return;
                                }
                                const field = event.currentTarget.querySelector("input[type=checkbox]");
                                if (field && !field.disabled) {
                                  field.click();
                                }
                              }}
                              onKeyDown={(event) => {
                                if (event.key !== "Enter" && event.key !== " ") {
                                  return;
                                }
                                event.preventDefault();
                                const field = event.currentTarget.querySelector("input[type=checkbox]");
                                if (field && !field.disabled) {
                                  field.click();
                                }
                              }}
                              style={{
                                cursor: loading || methodItemTypesLoading ? "default" : "pointer"
                              }}
                            >
                              <Field
                                name={`${part.key}.selected_items.${itemId}`}
                                component={CustomCheckbox}
                                disabled={loading || methodItemTypesLoading}
                                label={<span style={{ fontSize: "12.5px", lineHeight: "16px", color: "#212529" }}>{itemName}</span>}
                                className="compact-method-item-checkbox"
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
